import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import { Settings } from '../../../interfaces/Settings/Settings';
import { getSettingsByType, upsertSettings } from '../../../api/Settings';
import { SettingsType } from '../../../interfaces/Settings/SettingsType';
import { GenericSettingsValue } from '../../../interfaces/Settings/GenericSettingsValue';
import { getSettingsValue, getUpdatedSettings } from '../../../utils/Settings';
import { Waiting } from '../../../components/Waiting/Waiting';
import { sharedStyles } from '../../../utils/Style';
import { SettingsRow } from '../../../components/Settings/SettingsRow';
import { SettingsValueType } from '../../../interfaces/Settings/SettingsValueType';

export const ZiraatSettings = () => {
  const [settings, setSettings] = useState<Settings[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshSettings = () => {
    setLoading(true);
    getSettingsByType(SettingsType.ZIRAAT_POS)
      .then((s) => setSettings(s))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshSettings();
  }, []);

  const handleChange = useCallback(
    (key: string, newValue: GenericSettingsValue) =>
      setSettings(
        getUpdatedSettings(SettingsType.ZIRAAT_POS, key, settings, newValue),
      ),
    [settings],
  );

  const handleSave = () => {
    setLoading(true);
    upsertSettings(settings)
      .then(() => {
        toast.success('Ayarlar başarıyla kaydedildi');
        refreshSettings();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  return (
    <Paper
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '95%',
      }}
    >
      <Waiting open={loading} />
      <Typography
        sx={{
          ...sharedStyles.h5,
          fontWeight: 'bold',
        }}
      >
        Ziraat Bankası
      </Typography>
      <Divider />

      <SettingsRow
        name='Aktif'
        valueType={SettingsValueType.BOOLEAN}
        value={getSettingsValue(settings, 'ziraat_enabled')}
        onChange={(newValue) => handleChange('ziraat_enabled', newValue)}
      />
      <SettingsRow
        name='Başlık'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'ziraat_label')}
        onChange={(newValue) => handleChange('ziraat_label', newValue)}
      />
      <SettingsRow
        name='Açıklama'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'ziraat_description')}
        onChange={(newValue) => handleChange('ziraat_description', newValue)}
        multiline
      />
      <SettingsRow
        name='URL'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'ziraat_payment_url')}
        onChange={(newValue) => handleChange('ziraat_payment_url', newValue)}
      />
      <SettingsRow
        name='CLIENT_ID'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'ziraat_client_id')}
        onChange={(newValue) => handleChange('ziraat_client_id', newValue)}
      />
      <SettingsRow
        name='STORE_KEY'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'ziraat_storekey')}
        onChange={(newValue) => handleChange('ziraat_storekey', newValue)}
        masked
      />
      <SettingsRow
        name='STORE_TYPE'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'ziraat_storetype')}
        onChange={(newValue) => handleChange('ziraat_storetype', newValue)}
      />
      <SettingsRow
        name='PAYMENT_CURRENCY'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'ziraat_payment_currency')}
        onChange={(newValue) =>
          handleChange('ziraat_payment_currency', newValue)
        }
      />
      <SettingsRow
        name='PAYMENT_TXN_TYPE'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'ziraat_payment_transaction')}
        onChange={(newValue) =>
          handleChange('ziraat_payment_transaction', newValue)
        }
      />
      <SettingsRow
        name='PAYMENT_LANG'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'ziraat_payment_lang')}
        onChange={(newValue) => handleChange('ziraat_payment_lang', newValue)}
      />

      <Box sx={{ display: 'flex', flexGrow: 1 }} />
      <Box display='flex' justifyContent='flex-end'>
        <Button
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText, mt: 1.5 }}
        >
          Kaydet
        </Button>
      </Box>
    </Paper>
  );
};
